import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";
import axios from "axios";
import { BASE_URL } from "../../../services/configUrls";

const AddTopUpModal = ({ open, onClose, institute, onSuccess }) => {
  const [formData, setFormData] = useState({
    registration_limit: "",
    upload_invoice: null,
  });
  const [errors, setErrors] = useState({
    registration_limit: "",
  });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error on change
    setErrors((prev) => ({
      ...prev,
      [field]: "",
    }));
  };

  const validate = () => {
    const newErrors = { registration_limit: "" };
    let isValid = true;

    // Registration Limit validation
    if (!formData.registration_limit) {
      newErrors.registration_limit = "Registration Limit is required";
      isValid = false;
    } else if (
      !Number.isInteger(Number(formData.registration_limit)) ||
      Number(formData.registration_limit) <= 0
    ) {
      newErrors.registration_limit = "Must be a positive whole number";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = () => {
    if (validate()) {
      setConfirmOpen(true);
    }
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const payload = new FormData();
      payload.append("institute_id", institute?.institue_id);
      payload.append(
        "registration_limit",
        parseInt(formData.registration_limit, 10)
      );
      if (formData.upload_invoice) {
        payload.append("upload_invoice", formData.upload_invoice);
      }

      const response = await axios.post(
        `${BASE_URL}/accounts/add_topup`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setSnackbar({
        open: true,
        message: response.data?.detail || response.data?.message || "Top Up added successfully!",
        severity: "success",
      });

      setConfirmOpen(false);
      // Reset form
      setFormData({ registration_limit: "", upload_invoice: null });
      setErrors({ registration_limit: "" });
      // Notify parent to refresh topup status
      if (onSuccess) onSuccess();
      // Close modal after a brief delay so user sees success
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error("TopUp error:", error);
      setSnackbar({
        open: true,
        message:
          error.response?.data?.detail ||
          error.response?.data?.message ||
          "Failed to add Top Up. Please try again.",
        severity: "error",
      });
      setConfirmOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleClose = () => {
  setFormData({
    registration_limit: "",
    upload_invoice: null,
  });
  setErrors({
    registration_limit: "",
  });
  setConfirmOpen(false);
  onClose(); // existing close
};

  return (
    <>
      {/* Main Modal */}
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>Top Up</DialogTitle>

        <DialogContent>
          <TextField
            label="Registration Limit"
            required
            type="number"
            fullWidth
            margin="normal"
            value={formData.registration_limit}
            onChange={(e) =>
              handleChange("registration_limit", e.target.value)
            }
            error={!!errors.registration_limit}
            helperText={errors.registration_limit}
            inputProps={{ min: 1 }}
          />

          <Box sx={{ display: "flex", gap: 1, mt: 1, alignItems: "center" }}>
            <Button variant="outlined" component="label">
              {formData.upload_invoice
                ? `Invoice: ${formData.upload_invoice.name}`
                : "Upload Invoice"}
              <input
                type="file"
                hidden
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) =>
                  handleChange("upload_invoice", e.target.files?.[0] || null)
                }
              />
            </Button>

            {formData.upload_invoice && (
              <Button
                variant="text"
                color="error"
                onClick={() => handleChange("upload_invoice", null)}
              >
                Clear
              </Button>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{
              backgroundColor: "#3B82F6",
              color: "#fff",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#2563EB",
                boxShadow: "none",
              },
            }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Popup */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to proceed with the top up?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading ? "Processing..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddTopUpModal;